import React, { useEffect, useRef, useState } from 'react'
import { fetchShopifyItems, meta } from '../../../fnc.js'

const META_NAMESPACE = 'inventory'
const META_KEY = 'new_version'

const getImageSrc = (item) => {
	if (!item || !item.images_json) return null
	const images = typeof item.images_json === 'string'
		? (() => {
			try { return JSON.parse(item.images_json) } catch (e) { return [] }
		})()
		: item.images_json
	if (!Array.isArray(images) || images.length === 0) return null
	const image = images[0]
	return image && image.node ? (image.node.originalSrc || image.node.url) : (image && (image.src || image.url))
}

const getShopifyItem = (response, productId) => {
	const items = []
	if (response && Array.isArray(response.data)) items.push(...response.data)
	if (response) {
		Object.keys(response).forEach((type) => {
			if (response[type] && Array.isArray(response[type].data)) items.push(...response[type].data)
		})
	}
	return items.find(item => parseInt(item.id_product_shopify, 10) === parseInt(productId, 10)) || items[0] || null
}

const parseVersionValue = (value) => {
	try {
		const parsed = typeof value === 'string' ? JSON.parse(value) : value
		if (!parsed || !parsed.handle) return null
		return { id: parsed.id ? parseInt(parsed.id, 10) : null, handle: parsed.handle }
	} catch (e) {
		return null
	}
}

function NewVersionSection({ shop, parent, info, active }) {
	const inputSearch = useRef(null)
	const [loadingMeta, setLoadingMeta] = useState(false)
	const [saving, setSaving] = useState(false)
	const [searching, setSearching] = useState(false)
	const [searchResults, setSearchResults] = useState([])
	const [selectedVersion, setSelectedVersion] = useState(null)
	const [savedVersion, setSavedVersion] = useState(null)

	const openedProductId = parent && parent.match && parent.match.params
		? parseInt(parent.match.params.idItem, 10)
		: null

	const loadVersionMeta = () => {
		if (!openedProductId || !shop || !shop.id) return

		setLoadingMeta(true)
		meta.fetch({
			shop: shop.id,
			urlPrefix: `products/${openedProductId}/`,
			namespace: META_NAMESPACE,
			key: META_KEY,
		}, (response) => {
			setLoadingMeta(false)
			if (response && response.success) {
				const version = response.res && response.res.length > 0
					? parseVersionValue(response.res[0].value)
					: null
				setSavedVersion(version ? { ...version, imageSrc: null } : null)
				setSelectedVersion(null)

				if (version && version.id) {
					fetchShopifyItems({
						shop: shop.id,
						idShopifyList: String(version.id),
					}, (itemsResponse) => {
						const item = getShopifyItem(itemsResponse, version.id)
						setSavedVersion(current => current && current.id === version.id
							? { ...current, imageSrc: getImageSrc(item) }
							: current)
					})
				}
			} else if (response && response.error) {
				info({ error: response.error })
			}
		})
	}

	useEffect(() => {
		if (active) loadVersionMeta()
	}, [active, openedProductId, shop && shop.id])

	const searchProducts = (event) => {
		event.preventDefault()
		const search = inputSearch.current ? inputSearch.current.value.trim() : ''
		if (!search) {
			info({ error: 'Please fill the search input.' })
			return
		}

		setSearching(true)
		fetchShopifyItems({
			search,
			shop: shop.id,
			fromNbr: 0,
			nbrItem: 20,
			trigger: 'getItems_search'
		}, (response) => {
			setSearching(false)
			const results = []
			if (response) {
				if (response.success && Array.isArray(response.data)) results.push(...response.data)
				Object.keys(response).forEach((type) => {
					if (response[type] && response[type].success && Array.isArray(response[type].data)) {
						results.push(...response[type].data)
					}
				})
			}
			const uniqueResults = []
			const seenProductIds = new Set()
			results.forEach((item) => {
				const productId = parseInt(item.id_product_shopify, 10)
				if (productId !== openedProductId && !seenProductIds.has(productId)) {
					seenProductIds.add(productId)
					uniqueResults.push(item)
				}
			})
			setSearchResults(uniqueResults)
		})
	}

	const saveVersion = () => {
		if (!selectedVersion || !selectedVersion.id || !selectedVersion.handle) {
			info({ error: 'Select a product before saving the new version.' })
			return
		}

		setSaving(true)
		meta.create({
			shop: shop.id,
			urlPrefix: `products/${openedProductId}/`,
			namespace: META_NAMESPACE,
			key: META_KEY,
			value: {
				id: parseInt(selectedVersion.id, 10),
				handle: selectedVersion.handle,
			},
		}, (response) => {
			setSaving(false)
			if (response && response.success) {
				setSavedVersion({
					id: parseInt(selectedVersion.id, 10),
					handle: selectedVersion.handle,
					imageSrc: selectedVersion.imageSrc,
				})
				setSelectedVersion(null)
				setSearchResults([])
				if (inputSearch.current) inputSearch.current.value = ''
				info({ success: 'New version saved.' })
			} else {
				info({ error: response && response.error ? response.error : 'The new version could not be saved.' })
			}
		})
	}

	if (!openedProductId || !shop || !shop.id) return null

	return (
		<div>
			{loadingMeta ? (
				<div className="text-center p-4 text-muted">
					<span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true" />
					Loading new version...
				</div>
			) : (
				<>
					{savedVersion ? (
						<div className="alert alert-info d-flex align-items-center mb-3">
							{savedVersion.imageSrc ? (
								<img src={savedVersion.imageSrc} alt="New version product" width="56" height="56" className="mr-3 rounded" style={{ objectFit: 'contain', backgroundColor: '#fff' }} />
							) : null}
							<div className="d-flex flex-column">
								<strong>Current new version</strong>
								<span>{savedVersion.id ? `Product ID: ${savedVersion.id}` : 'New product not created yet'}</span>
								<a href={`https://${shop.account_name}/products/${savedVersion.handle}`} target="_blank" rel="noopener noreferrer">
									/{savedVersion.handle}
								</a>
							</div>
						</div>
					) : (
						<div className="alert alert-secondary">No new version is attached to this product yet.</div>
					)}

					<form onSubmit={searchProducts}>
						<div className="input-group mb-3">
							<input ref={inputSearch} type="search" className="form-control" placeholder="Search product by title or SKU" aria-label="Search product by title or SKU" />
							<div className="input-group-append">
								<button type="submit" className="btn btn-outline-secondary" disabled={searching}>
									{searching ? 'Searching...' : 'Search'}
								</button>
							</div>
						</div>
					</form>

					{searchResults.length > 0 && (
						<div className="list-group mb-3">
							{searchResults.map(item => {
								const itemId = parseInt(item.id_product_shopify, 10)
								const selected = selectedVersion && selectedVersion.id === itemId
								const imageSrc = getImageSrc(item)
								return (
									<button type="button" key={itemId} className={`list-group-item list-group-item-action d-flex align-items-center ${selected ? 'active' : ''}`} style={{ minWidth: 0, whiteSpace: 'normal', textAlign: 'left' }} onClick={() => setSelectedVersion({ id: itemId, handle: item.handle, imageSrc })}>
										{imageSrc ? <img src={imageSrc} alt="" width="45" height="45" className="mr-3 rounded" style={{ objectFit: 'contain', backgroundColor: '#fff' }} /> : null}
										<span style={{ minWidth: 0, overflowWrap: 'anywhere' }}>[{item.sku || itemId}] {item.title}</span>
									</button>
								)
							})}
						</div>
					)}

			{selectedVersion && (
						<div className="alert alert-primary d-flex align-items-center justify-content-between flex-wrap mb-3" style={{ gap: '12px', whiteSpace: "normal" }}>
							<div className="d-flex align-items-center">
								{selectedVersion.imageSrc ? (
									<img src={selectedVersion.imageSrc} alt="Selected product" width="45" height="45" className="mr-3 rounded" style={{ objectFit: 'contain', backgroundColor: '#fff' }} />
								) : null}
								<span>Selected product: {selectedVersion.id} / {selectedVersion.handle}</span>
							</div>
							<button type="button" className="btn btn-primary ml-auto" onClick={saveVersion} disabled={saving}>
								{saving ? 'Saving...' : savedVersion ? 'Replace new version' : 'Save new version'}
							</button>
						</div>
					)}
				</>
			)}
		</div>
	)
}

export default NewVersionSection
